import { FilterValue } from "antd/es/table/interface";
import { useState, useCallback } from "react";

type FilterOperator =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "like"
  | "ilike"
  | "in"
  | "nin"
  | "between"
  | "is_null"
  | "is_not_null";

export type FilterCondition = {
  field: string;
  operator: FilterOperator;
  values?: FilterValue;
};

const useFilter = () => {
  const [filters, setFilters] = useState<FilterCondition[]>([]);

  // Add or update a filter condition
  const setFieldFilter = useCallback(
    ({ field, operator, values }: FilterCondition) => {
      setFilters((prev) => {
        // Remove existing filters for this field
        const filtered = prev.filter((f) => f.field !== field);

        // Add new filter
        const newFilter: FilterCondition = { field, operator };
        if (values !== undefined) {
          newFilter.values = values;
        }

        return [...filtered, newFilter];
      });
    },
    []
  );

  // Remove filter for a specific field
  const removeFieldFilter = useCallback(
    (field: string) =>
      setFilters((prev) => prev.filter((f) => f.field !== field)),
    []
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters([]);
  }, []);

  // Get filter for a specific field
  const getFieldFilter = useCallback(
    (field: string) => filters.find((f) => f.field === field),
    [filters]
  );

  // Get filter values for table columns
  const getFilterValues = useCallback(() => {
    return filters.reduce((acc, { field, values }) => {
      if (values) {
        acc[field] = values;
      }
      return acc;
    }, {} as Record<string, FilterValue>);
  }, [filters]);

  return {
    filters,
    setFilters,
    setFieldFilter,
    removeFieldFilter,
    clearFilters,
    getFieldFilter,
    getFilterValues,
  };
};

export default useFilter;
